import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import type { ColorProperty } from '@src/types';

interface ColorPropertiesTableProps {
  data: ColorProperty[];
}

export const ColorPropertiesTable: React.FC<ColorPropertiesTableProps> = ({ data }) => {
  const navigate = useNavigate();

  const getStatus = (property: ColorProperty): 'complete' | 'incomplete' => {
    // A color property is complete if it has hex_code and at least one family group
    const hasHex = !!property.hex_code;
    const hasGroup = property.family && property.family.length > 0;
    return hasHex && hasGroup ? 'complete' : 'incomplete';
  };

  const handleRowClick = (property: ColorProperty) => {
    navigate(`/catalog/attributes/colors/${property.id}`);
  };

  return (
    <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem' }}>
              Code
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem' }}>
              Swatch
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem' }}>
              Display Name
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem' }}>
              Color Groups
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem' }}>
              Hex
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem' }}>
              Synonyms
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem' }}>
              Status
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((property) => {
            const status = getStatus(property);
            return (
              <TableRow
                key={property.id}
                hover
                onClick={() => handleRowClick(property)}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <TableCell>{property.code}</TableCell>
                <TableCell>
                  {property.url ? (
                    <Box
                      component="img"
                      src={property.url}
                      alt={property.display}
                      sx={{
                        width: 40,
                        height: 40,
                        objectFit: 'cover',
                        border: '1px solid #ddd',
                        borderRadius: 1,
                      }}
                    />
                  ) : property.hex_code ? (
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: property.hex_code,
                        border: '1px solid #ddd',
                        borderRadius: 1,
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{property.display}</TableCell>
                <TableCell>
                  {property.family && property.family.length > 0
                    ? property.family.join(', ')
                    : '—'}
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                  >
                    {property.hex_code || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {property.synonyms && property.synonyms.length > 0
                    ? property.synonyms.join(', ')
                    : '—'}
                </TableCell>
                <TableCell>
                  <Chip
                    label={status === 'complete' ? 'Complete' : 'Incomplete'}
                    color={status === 'complete' ? 'success' : 'warning'}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
